import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
  updateDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { app } from '../../firebase';
import { getAllPosts as getStaticPosts } from '../../content/blog';
import {
  Plus,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  Calendar,
  Clock,
  Loader2,
  Upload,
} from 'lucide-react';

const AdminBlogList = ({ onEdit, onCreate }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const db = getFirestore(app);

  useEffect(() => {
    const q = query(collection(db, 'blog_posts'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPosts(
        snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate(),
          updatedAt: d.data().updatedAt?.toDate(),
          publishedAt: d.data().publishedAt?.toDate(),
        }))
      );
      setLoading(false);
    });
    return () => unsubscribe();
  }, [db]);

  const toggleStatus = async (post) => {
    const newStatus = post.status === 'published' ? 'draft' : 'published';
    await updateDoc(doc(db, 'blog_posts', post.id), {
      status: newStatus,
      ...(newStatus === 'published' && !post.publishedAt
        ? { publishedAt: new Date() }
        : {}),
    });
  };

  const seedPosts = async () => {
    setSeeding(true);
    try {
      const staticPosts = getStaticPosts();
      for (const sp of staticPosts) {
        const pubDate = new Date(sp.date);
        await addDoc(collection(db, 'blog_posts'), {
          slug: sp.slug,
          title: sp.title,
          description: sp.description,
          content: sp.content,
          category: sp.category,
          gradient: sp.gradient,
          author: sp.author,
          readTime: sp.readTime,
          status: 'published',
          createdAt: Timestamp.fromDate(pubDate),
          updatedAt: Timestamp.fromDate(pubDate),
          publishedAt: Timestamp.fromDate(pubDate),
        });
      }
    } catch (err) {
      console.error('Seed failed:', err);
    }
    setSeeding(false);
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setDeleting(post.id);
    try {
      await deleteDoc(doc(db, 'blog_posts', post.id));
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleting(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-display font-bold text-white">Blog Posts</h2>
          <p className="text-slate-400 text-sm mt-1">
            {posts.length} post{posts.length !== 1 ? 's' : ''} &middot;{' '}
            {posts.filter((p) => p.status === 'published').length} published
          </p>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          New Post
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-20 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
          <p className="text-slate-400">No blog posts yet.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              onClick={onCreate}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold"
            >
              Create your first post
            </button>
            <span className="text-slate-600">or</span>
            <button
              onClick={seedPosts}
              disabled={seeding}
              className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 text-sm font-semibold disabled:opacity-50"
            >
              {seeding ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {seeding ? 'Importing…' : 'Import starter posts'}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <div
              key={post.id}
              className="flex items-center gap-4 rounded-xl border border-white/[0.06] bg-white/[0.03] p-5 hover:bg-white/[0.05] transition-colors"
            >
              {/* Status indicator */}
              <div
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  post.status === 'published' ? 'bg-emerald-400' : 'bg-amber-400'
                }`}
              />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold truncate">{post.title}</h3>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {post.createdAt?.toLocaleDateString() || '—'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readTime || '—'}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                      post.status === 'published'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'bg-amber-500/10 text-amber-400'
                    }`}
                  >
                    {post.status}
                  </span>
                  {post.category && (
                    <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 text-[10px] font-semibold uppercase tracking-wider">
                      {post.category}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => toggleStatus(post)}
                  className="p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors"
                  title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                >
                  {post.status === 'published' ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => onEdit(post)}
                  className="p-2 rounded-lg hover:bg-white/[0.06] text-slate-400 hover:text-white transition-colors"
                  title="Edit"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(post)}
                  disabled={deleting === post.id}
                  className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  {deleting === post.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlogList;
